import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { format, addDays, parse, isAfter, isBefore } from 'date-fns';
import { it } from 'date-fns/locale';
import axios from 'axios';
import { 
  Zap, LogOut, Calendar as CalendarIcon, Clock, Plus, 
  Bike, FileText, User, History, AlertCircle, CheckCircle2,
  XCircle, Loader2, ImageIcon, ZoomIn, Upload, Pencil, Trash2, Filter, Car
} from 'lucide-react';
import LoadingOverlay from '@/components/LoadingOverlay';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DELIVERY_SLOTS = ["10:30", "11:00", "11:30", "12:00", "12:30", "13:00", "13:30", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00"];

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateAvailability, setDateAvailability] = useState(null);
  const [pickupSlots, setPickupSlots] = useState([]);
  const [dailyLimit, setDailyLimit] = useState(5);
  const [fullyBookedDates, setFullyBookedDates] = useState([]);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('bookings');
  
  // Model series for vehicle form
  const [modelSeries, setModelSeries] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState('');
  const [filteredModels, setFilteredModels] = useState([]);
  
  // Vehicle dialog state
  const [isVehicleDialogOpen, setIsVehicleDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleForm, setVehicleForm] = useState({
    modello_moto: '',
    nickname: '',
    libretto_base64: null
  });
  const [vehicleLibrettoFile, setVehicleLibrettoFile] = useState(null);
  const [vehicleLibrettoPreview, setVehicleLibrettoPreview] = useState(null);
  const [isSavingVehicle, setIsSavingVehicle] = useState(false);
  
  // Delete vehicle dialog
  const [isDeleteVehicleDialogOpen, setIsDeleteVehicleDialogOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [isDeletingVehicle, setIsDeletingVehicle] = useState(false);
  
  // Image viewer state
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [viewerImage, setViewerImage] = useState(null);
  const [viewerTitle, setViewerTitle] = useState('');
  
  const [bookingForm, setBookingForm] = useState({
    vehicle_id: '',
    data_consegna: '',
    ora_consegna: '',
    ora_ritiro: '',
    km_attuali: '',
    note: ''
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setIsLoading(true);
    await Promise.all([
      fetchBookings(),
      fetchVehicles(),
      fetchDailyLimit(),
      fetchCustomerProfile(),
      fetchModelSeries()
    ]);
    setIsLoading(false);
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API_URL}/bookings/customer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data.bookings);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API_URL}/customers/me/vehicles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVehicles(response.data.vehicles);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const fetchCustomerProfile = async () => {
    try {
      const response = await axios.get(`${API_URL}/customer/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomerProfile(response.data.customer);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchModelSeries = async () => {
    try {
      const response = await axios.get(`${API_URL}/model-series`);
      setModelSeries(response.data.series);
    } catch (error) {
      console.error('Error fetching model series:', error);
    }
  };

  // Filter models when series changes
  useEffect(() => {
    if (selectedSeries && selectedSeries !== 'all') {
      const series = modelSeries.find(s => s.id === selectedSeries);
      if (series) {
        setFilteredModels(series.models);
      }
    } else {
      const allModels = [];
      modelSeries.forEach(series => {
        series.models.forEach(model => {
          allModels.push({
            ...model,
            seriesName: series.name
          });
        });
      });
      setFilteredModels(allModels);
    }
  }, [selectedSeries, modelSeries]);

  // Image viewer function
  const openImageViewer = (imageBase64, title) => {
    setViewerImage(imageBase64);
    setViewerTitle(title);
    setIsImageViewerOpen(true);
  };

  const fetchDailyLimit = async () => {
    try {
      const response = await axios.get(`${API_URL}/shop/daily-limit`);
      setDailyLimit(response.data.limite_giornaliero);
    } catch (error) {
      console.error('Error fetching daily limit:', error);
    }
  };

  const checkDateAvailability = async (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    try {
      const response = await axios.get(`${API_URL}/bookings/date/${dateStr}/availability`);
      setDateAvailability(response.data);
      if (!response.data.is_available) {
        setFullyBookedDates(prev => [...prev, dateStr]);
      }
      return response.data.is_available;
    } catch (error) {
      console.error('Error checking availability:', error);
      return false;
    }
  };

  const handleDateSelect = async (date) => {
    setSelectedDate(date);
    const dateStr = format(date, 'yyyy-MM-dd');
    setBookingForm({ ...bookingForm, data_consegna: dateStr, ora_consegna: '', ora_ritiro: '' });
    await checkDateAvailability(date);
  };

  const handleDeliveryTimeChange = (time) => {
    setBookingForm({ ...bookingForm, ora_consegna: time, ora_ritiro: '' });
    
    const deliveryTime = parse(time, 'HH:mm', new Date());
    const minPickupTime = addDays(deliveryTime, 0);
    minPickupTime.setMinutes(minPickupTime.getMinutes() + 90);
    
    const slots = [];
    const endTime = parse('18:00', 'HH:mm', new Date());
    const lunchStart = parse('13:30', 'HH:mm', new Date());
    const lunchEnd = parse('14:30', 'HH:mm', new Date());
    
    let currentTime = new Date(minPickupTime);
    
    while (isBefore(currentTime, endTime) || currentTime.getHours() === endTime.getHours() && currentTime.getMinutes() === endTime.getMinutes()) {
      const timeStr = format(currentTime, 'HH:mm');
      const currentParsed = parse(timeStr, 'HH:mm', new Date());
      
      if (!(isAfter(currentParsed, lunchStart) && isBefore(currentParsed, lunchEnd)) && 
          !(currentParsed.getHours() === lunchStart.getHours() && currentParsed.getMinutes() === lunchStart.getMinutes())) {
        slots.push(timeStr);
      }
      
      currentTime.setMinutes(currentTime.getMinutes() + 15);
    }
    
    setPickupSlots(slots);
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/bookings`, {
        ...bookingForm,
        km_attuali: parseInt(bookingForm.km_attuali),
        note: bookingForm.note || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Prenotazione inviata! Riceverai conferma via email.');
      setIsBookingOpen(false);
      setBookingForm({
        vehicle_id: '',
        data_consegna: '',
        ora_consegna: '',
        ora_ritiro: '',
        km_attuali: '',
        note: ''
      });
      setSelectedDate(null);
      setDateAvailability(null);
      fetchBookings();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante la prenotazione';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Vehicle CRUD functions
  const openAddVehicleDialog = () => {
    setEditingVehicle(null);
    setVehicleForm({ modello_moto: '', nickname: '', libretto_base64: null });
    setVehicleLibrettoFile(null);
    setVehicleLibrettoPreview(null);
    setSelectedSeries('');
    setIsVehicleDialogOpen(true);
  };

  const openEditVehicleDialog = (vehicle) => {
    setEditingVehicle(vehicle);
    setVehicleForm({
      modello_moto: vehicle.modello_moto,
      nickname: vehicle.nickname || '',
      libretto_base64: null
    });
    setVehicleLibrettoFile(null);
    setVehicleLibrettoPreview(vehicle.libretto_image ? `data:image/jpeg;base64,${vehicle.libretto_image}` : null);
    setSelectedSeries('');
    setIsVehicleDialogOpen(true);
  };

  const handleVehicleLibrettoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setVehicleLibrettoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setVehicleLibrettoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveVehicle = async () => {
    if (!vehicleForm.modello_moto) {
      toast.error('Seleziona un modello di moto');
      return;
    }

    setIsSavingVehicle(true);
    try {
      let libretto_base64 = null;
      if (vehicleLibrettoFile) {
        libretto_base64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          };
          reader.readAsDataURL(vehicleLibrettoFile);
        });
      }

      const payload = {
        modello_moto: vehicleForm.modello_moto,
        nickname: vehicleForm.nickname || null,
        libretto_base64
      };

      if (editingVehicle) {
        await axios.put(`${API_URL}/customers/me/vehicles/${editingVehicle.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Veicolo aggiornato con successo');
      } else {
        await axios.post(`${API_URL}/customers/me/vehicles`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Veicolo aggiunto con successo');
      }

      setIsVehicleDialogOpen(false);
      fetchVehicles();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nel salvataggio del veicolo';
      toast.error(message);
    } finally {
      setIsSavingVehicle(false);
    }
  };

  const handleDeleteVehicle = async () => {
    if (!vehicleToDelete) return;
    
    setIsDeletingVehicle(true);
    try {
      await axios.delete(`${API_URL}/customers/me/vehicles/${vehicleToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Veicolo eliminato con successo');
      setIsDeleteVehicleDialogOpen(false);
      setVehicleToDelete(null);
      fetchVehicles();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nell\'eliminazione del veicolo';
      toast.error(message);
    } finally {
      setIsDeletingVehicle(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending_confirmation: { label: 'In Attesa', variant: 'warning', icon: <Loader2 className="w-3 h-3 animate-spin" /> },
      confirmed: { label: 'Confermato', variant: 'info', icon: <CheckCircle2 className="w-3 h-3" /> },
      completed: { label: 'Completato', variant: 'success', icon: <CheckCircle2 className="w-3 h-3" /> },
      cancelled: { label: 'Annullato', variant: 'destructive', icon: <XCircle className="w-3 h-3" /> }
    };
    
    const config = statusConfig[status] || statusConfig.pending_confirmation;
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  const getModelName = (modelId) => {
    for (const series of modelSeries) {
      const model = series.models.find(m => m.id === modelId);
      if (model) return model.name;
    }
    return modelId;
  };

  const minDate = addDays(new Date(), 1);
  const selectedVehicle = vehicles.find(v => v.id === bookingForm.vehicle_id);

  return (
    <div className="min-h-screen bg-background" data-testid="customer-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border/50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Zap className="w-8 h-8 text-primary" />
              <span className="font-heading font-black text-xl hidden sm:inline">NIU Service</span>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-muted-foreground" />
                <span>{user?.nome} {user?.cognome}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Esci
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-6 py-8">
        {/* Welcome Card */}
        <Card className="mb-8 bg-primary text-primary-foreground">
          <CardContent className="p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2">
                  Ciao, {user?.nome}!
                </h1>
                <p className="text-primary-foreground/80">
                  Gestisci i tuoi veicoli e le prenotazioni tagliandi
                </p>
              </div>
              <Button 
                size="lg" 
                variant="secondary" 
                className="font-bold" 
                onClick={() => vehicles.length > 0 ? setIsBookingOpen(true) : toast.error('Aggiungi prima un veicolo per prenotare')}
                data-testid="new-booking-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Nuovo Tagliando
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <Car className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">I Miei Veicoli</p>
                <p className="font-bold">{vehicles.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-500/10 text-blue-500 flex items-center justify-center">
                <CalendarIcon className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Prenotazioni</p>
                <p className="font-bold">{bookings.length}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="sm:col-span-2 lg:col-span-1">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-500/10 text-green-500 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completati</p>
                <p className="font-bold">{bookings.filter(b => b.status === 'completed').length}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="bookings" className="flex items-center gap-2">
              <History className="w-4 h-4" />
              Prenotazioni
            </TabsTrigger>
            <TabsTrigger value="vehicles" className="flex items-center gap-2">
              <Bike className="w-4 h-4" />
              I Miei Veicoli
            </TabsTrigger>
          </TabsList>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Le Tue Prenotazioni
                </CardTitle>
                <CardDescription>
                  Storico e stato dei tagliandi
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : bookings.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-2">Nessuna prenotazione trovata</p>
                    {vehicles.length === 0 ? (
                      <p className="text-sm text-muted-foreground mb-4">Aggiungi un veicolo per prenotare il tuo primo tagliando</p>
                    ) : (
                      <Button 
                        className="mt-4" 
                        onClick={() => setIsBookingOpen(true)}
                        data-testid="empty-state-new-booking-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Prenota il tuo primo tagliando
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {bookings.map((booking) => (
                      <Card key={booking.id} className="border-border/50" data-testid={`booking-card-${booking.id}`}>
                        <CardContent className="p-4 md:p-6">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="space-y-2">
                              <div className="flex items-center gap-3">
                                <CalendarIcon className="w-5 h-5 text-primary" />
                                <span className="font-bold">
                                  {format(new Date(booking.data_consegna), 'EEEE d MMMM yyyy', { locale: it })}
                                </span>
                                {getStatusBadge(booking.status)}
                              </div>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Bike className="w-4 h-4" />
                                  {booking.modello_moto}
                                </span>
                                <span>Consegna: {booking.ora_consegna}</span>
                                <span>Ritiro: {booking.ora_ritiro}</span>
                                <span>Km: {booking.km_attuali}</span>
                              </div>
                              {booking.note && (
                                <p className="text-sm text-muted-foreground italic">"{booking.note}"</p>
                              )}
                              {booking.status === 'completed' && booking.lavori_effettuati && (
                                <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                  <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-1">Lavori effettuati:</p>
                                  <p className="text-sm text-green-600 dark:text-green-400 whitespace-pre-line">
                                    {booking.lavori_effettuati}
                                  </p>
                                </div>
                              )}
                              {booking.status === 'completed' && booking.foto_bolla && (
                                <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                                  <p className="text-sm font-medium text-green-700 dark:text-green-300 mb-2 flex items-center gap-1">
                                    <ImageIcon className="w-4 h-4" /> Foto Bolla Lavori
                                  </p>
                                  <div 
                                    className="relative inline-block cursor-pointer group"
                                    onClick={() => openImageViewer(booking.foto_bolla, `Bolla Lavori - ${new Date(booking.data_consegna).toLocaleDateString('it-IT')}`)}
                                  >
                                    <img 
                                      src={`data:image/jpeg;base64,${booking.foto_bolla}`}
                                      alt="Bolla lavori"
                                      className="max-h-32 rounded-lg border border-green-300 shadow-sm group-hover:opacity-90 transition-opacity"
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                      <div className="bg-black/50 rounded-full p-2">
                                        <ZoomIn className="w-4 h-4 text-white" />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {booking.status === 'cancelled' && booking.motivo_annullamento && (
                                <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                                  <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">Motivo annullamento:</p>
                                  <p className="text-sm text-red-600 dark:text-red-400">
                                    {booking.motivo_annullamento}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Vehicles Tab */}
          <TabsContent value="vehicles">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Bike className="w-5 h-5" />
                      I Miei Veicoli
                    </CardTitle>
                    <CardDescription>
                      Gestisci i tuoi veicoli NIU
                    </CardDescription>
                  </div>
                  <Button onClick={openAddVehicleDialog} data-testid="add-vehicle-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Aggiungi Veicolo
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  </div>
                ) : vehicles.length === 0 ? (
                  <div className="text-center py-12">
                    <Bike className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-2">Nessun veicolo registrato</p>
                    <p className="text-sm text-muted-foreground mb-4">Aggiungi il tuo primo veicolo per poter prenotare i tagliandi</p>
                    <Button onClick={openAddVehicleDialog} data-testid="empty-state-add-vehicle-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Aggiungi il tuo primo veicolo
                    </Button>
                  </div>
                ) : (
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {vehicles.map((vehicle) => (
                      <Card key={vehicle.id} className="border-border/50 hover:border-primary/30 transition-colors" data-testid={`vehicle-card-${vehicle.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                                <Bike className="w-5 h-5" />
                              </div>
                              <div>
                                <p className="font-bold">{getModelName(vehicle.modello_moto)}</p>
                                {vehicle.nickname && (
                                  <p className="text-sm text-muted-foreground">"{vehicle.nickname}"</p>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="icon" onClick={() => openEditVehicleDialog(vehicle)} data-testid={`edit-vehicle-${vehicle.id}`}>
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => {
                                  setVehicleToDelete(vehicle);
                                  setIsDeleteVehicleDialogOpen(true);
                                }}
                                data-testid={`delete-vehicle-${vehicle.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          
                          {vehicle.libretto_data && (
                            <div className="space-y-1 text-sm border-t pt-3 mt-3">
                              {vehicle.libretto_data.targa && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Targa:</span>
                                  <span className="font-medium">{vehicle.libretto_data.targa}</span>
                                </div>
                              )}
                              {vehicle.libretto_data.telaio && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Telaio:</span>
                                  <span className="font-medium text-xs">{vehicle.libretto_data.telaio}</span>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {vehicle.libretto_image && (
                            <div className="mt-3 pt-3 border-t">
                              <div 
                                className="relative cursor-pointer group"
                                onClick={() => openImageViewer(vehicle.libretto_image, `Libretto - ${getModelName(vehicle.modello_moto)}`)}
                              >
                                <img 
                                  src={`data:image/jpeg;base64,${vehicle.libretto_image}`}
                                  alt="Libretto"
                                  className="w-full h-24 object-cover rounded-lg group-hover:opacity-90 transition-opacity"
                                />
                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  <div className="bg-black/50 rounded-full p-2">
                                    <ZoomIn className="w-4 h-4 text-white" />
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Booking Dialog */}
      <Dialog open={isBookingOpen} onOpenChange={setIsBookingOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Prenota Tagliando</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitBooking} className="space-y-6 mt-4">
            {/* Vehicle Selection */}
            <div className="space-y-2">
              <Label>Seleziona Veicolo *</Label>
              <Select 
                value={bookingForm.vehicle_id} 
                onValueChange={(value) => setBookingForm({ ...bookingForm, vehicle_id: value })}
              >
                <SelectTrigger data-testid="vehicle-select">
                  <Bike className="w-4 h-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Seleziona un veicolo" />
                </SelectTrigger>
                <SelectContent>
                  {vehicles.map((vehicle) => (
                    <SelectItem key={vehicle.id} value={vehicle.id}>
                      <div className="flex items-center gap-2">
                        <span>{getModelName(vehicle.modello_moto)}</span>
                        {vehicle.nickname && (
                          <span className="text-muted-foreground text-sm">({vehicle.nickname})</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {vehicles.length === 0 && (
                <p className="text-sm text-red-500">Devi aggiungere un veicolo prima di prenotare</p>
              )}
            </div>

            {/* Date Selection */}
            {bookingForm.vehicle_id && (
              <div className="space-y-2">
                <Label>Seleziona Data *</Label>
                <p className="text-xs text-muted-foreground">
                  Prenotazioni disponibili: Martedì - Venerdì
                </p>
                <div className="border rounded-lg p-4">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={(date) => {
                      if (date < minDate) return true;
                      const day = date.getDay();
                      return day === 0 || day === 1 || day === 6;
                    }}
                    locale={it}
                    className="mx-auto"
                    data-testid="booking-calendar"
                  />
                </div>
                {dateAvailability && (
                  <div className={`flex items-center gap-2 text-sm ${dateAvailability.is_available ? 'text-green-600' : 'text-red-600'}`}>
                    {dateAvailability.is_available ? (
                      <>
                        <CheckCircle2 className="w-4 h-4" />
                        <span>{dateAvailability.available_slots} posti disponibili</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4" />
                        <span>Giornata completa</span>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Delivery Time */}
            {selectedDate && dateAvailability?.is_available && (
              <div className="space-y-2">
                <Label>Ora Consegna Veicolo *</Label>
                <Select 
                  value={bookingForm.ora_consegna} 
                  onValueChange={handleDeliveryTimeChange}
                >
                  <SelectTrigger data-testid="delivery-time-select">
                    <Clock className="w-4 h-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="Seleziona ora" />
                  </SelectTrigger>
                  <SelectContent>
                    {DELIVERY_SLOTS.map((slot) => (
                      <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Pickup Time */}
            {bookingForm.ora_consegna && pickupSlots.length > 0 && (
              <div className="space-y-2">
                <Label>Ora Ritiro Veicolo *</Label>
                <Select 
                  value={bookingForm.ora_ritiro} 
                  onValueChange={(value) => setBookingForm({ ...bookingForm, ora_ritiro: value })}
                >
                  <SelectTrigger data-testid="pickup-time-select">
                    <Clock className="w-4 h-4 mr-2 text-muted-foreground" />
                    <SelectValue placeholder="Seleziona ora ritiro" />
                  </SelectTrigger>
                  <SelectContent>
                    {pickupSlots.map((slot) => (
                      <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Minimo 1h30 dopo la consegna, esclusa pausa pranzo (13:30-14:30)
                </p>
              </div>
            )}

            {/* KM */}
            <div className="space-y-2">
              <Label>Km Attuali *</Label>
              <Input
                type="number"
                placeholder="es. 12500"
                value={bookingForm.km_attuali}
                onChange={(e) => setBookingForm({ ...bookingForm, km_attuali: e.target.value })}
                required
                data-testid="km-input"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Note (opzionale)</Label>
              <Textarea
                placeholder="Segnala eventuali problemi o richieste specifiche..."
                value={bookingForm.note}
                onChange={(e) => setBookingForm({ ...bookingForm, note: e.target.value })}
                rows={3}
                data-testid="notes-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 font-bold"
              disabled={isSubmitting || !bookingForm.vehicle_id || !bookingForm.data_consegna || !bookingForm.ora_consegna || !bookingForm.ora_ritiro || !bookingForm.km_attuali}
              data-testid="submit-booking-btn"
            >
              {isSubmitting ? 'Invio in corso...' : 'Prenota Tagliando'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vehicle Add/Edit Dialog */}
      <Dialog open={isVehicleDialogOpen} onOpenChange={setIsVehicleDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {editingVehicle ? 'Modifica Veicolo' : 'Aggiungi Veicolo'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Series Filter */}
            <div className="space-y-2">
              <Label>Filtra per Serie</Label>
              <Select 
                value={selectedSeries} 
                onValueChange={setSelectedSeries}
              >
                <SelectTrigger data-testid="vehicle-series-select">
                  <Filter className="w-4 h-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Tutte le serie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le serie</SelectItem>
                  {modelSeries.map((series) => (
                    <SelectItem key={series.id} value={series.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{series.name}</span>
                        <span className="text-xs text-muted-foreground">({series.models.length})</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Model Selection */}
            <div className="space-y-2">
              <Label>Modello *</Label>
              <Select 
                value={vehicleForm.modello_moto} 
                onValueChange={(value) => setVehicleForm({ ...vehicleForm, modello_moto: value })}
              >
                <SelectTrigger data-testid="vehicle-model-select">
                  <Bike className="w-4 h-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Seleziona modello" />
                </SelectTrigger>
                <SelectContent>
                  {selectedSeries && selectedSeries !== 'all' ? (
                    filteredModels.map((model) => (
                      <SelectItem key={model.id} value={model.id}>
                        <div className="flex items-center gap-3">
                          <img src={model.image} alt={model.name} className="w-10 h-10 object-cover rounded" />
                          <span>{model.name}</span>
                        </div>
                      </SelectItem>
                    ))
                  ) : (
                    modelSeries.map((series) => (
                      <div key={series.id}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-primary uppercase tracking-wider bg-primary/5">
                          {series.name}
                        </div>
                        {series.models.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            <div className="flex items-center gap-3">
                              <img src={model.image} alt={model.name} className="w-10 h-10 object-cover rounded" />
                              <span>{model.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </div>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Nickname */}
            <div className="space-y-2">
              <Label>Soprannome (opzionale)</Label>
              <Input
                placeholder='es. "La mia NQi"'
                value={vehicleForm.nickname}
                onChange={(e) => setVehicleForm({ ...vehicleForm, nickname: e.target.value })}
                data-testid="vehicle-nickname-input"
              />
            </div>

            {/* Libretto Upload */}
            <div className="space-y-2">
              <Label>Foto Libretto (opzionale)</Label>
              <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  id="vehicle-libretto-upload"
                  onChange={handleVehicleLibrettoChange}
                  data-testid="vehicle-libretto-input"
                />
                <label htmlFor="vehicle-libretto-upload" className="cursor-pointer">
                  {vehicleLibrettoPreview ? (
                    <div className="space-y-2">
                      <img 
                        src={vehicleLibrettoPreview} 
                        alt="Anteprima libretto" 
                        className="max-h-32 mx-auto rounded"
                      />
                      <p className="text-sm text-muted-foreground">Clicca per cambiare</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-8 h-8 mx-auto text-muted-foreground" />
                      <p className="text-sm text-muted-foreground">
                        Carica foto del libretto
                      </p>
                      <p className="text-xs text-muted-foreground">
                        I dati verranno estratti automaticamente
                      </p>
                    </div>
                  )}
                </label>
              </div>
            </div>
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setIsVehicleDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSaveVehicle} 
              disabled={isSavingVehicle || !vehicleForm.modello_moto}
              data-testid="save-vehicle-btn"
            >
              {isSavingVehicle ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Salvataggio...
                </>
              ) : (
                editingVehicle ? 'Salva Modifiche' : 'Aggiungi Veicolo'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Vehicle Confirmation Dialog */}
      <Dialog open={isDeleteVehicleDialogOpen} onOpenChange={setIsDeleteVehicleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Elimina Veicolo</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare questo veicolo? Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          {vehicleToDelete && (
            <div className="p-4 bg-muted rounded-lg">
              <p className="font-medium">{getModelName(vehicleToDelete.modello_moto)}</p>
              {vehicleToDelete.nickname && (
                <p className="text-sm text-muted-foreground">"{vehicleToDelete.nickname}"</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteVehicleDialogOpen(false)}>
              Annulla
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteVehicle}
              disabled={isDeletingVehicle}
              data-testid="confirm-delete-vehicle-btn"
            >
              {isDeletingVehicle ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Eliminazione...
                </>
              ) : (
                'Elimina Veicolo'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog open={isImageViewerOpen} onOpenChange={setIsImageViewerOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[95vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              {viewerTitle}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex items-center justify-center p-4 bg-muted/20 rounded-lg">
            {viewerImage && (
              <img 
                src={`data:image/jpeg;base64,${viewerImage}`}
                alt={viewerTitle}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Loading Overlay */}
      <LoadingOverlay 
        isVisible={isSavingVehicle && vehicleLibrettoFile}
        message="Elaborazione libretto..."
        submessage="Stiamo estraendo i dati dal documento con l'AI"
      />
    </div>
  );
};

export default CustomerDashboard;
